import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { ArrowLeft } from "lucide-react";

export default function SessionReport() {
    const { id } = useParams();
    const { t } = useLang();
    const { user } = useAuth();
    const aiName = (user?.ai_name || "IA").trim() || "IA";
    const [sess, setSess] = useState(null);

    useEffect(() => {
        api.get(`/sessions/${id}`).then((r) => setSess(r.data));
    }, [id]);

    if (!sess) return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <p className="p-10 text-slate-500">{t("common.loading")}</p>
        </div>
    );

    const scores = sess.scores || {};
    const fillers = sess.filler_counts || {};
    const fillerEntries = Object.entries(fillers).filter(([k]) => k !== "_total");
    const startedAt = sess.started_at ? new Date(sess.started_at) : null;
    const endedAt = sess.ended_at ? new Date(sess.ended_at) : null;
    const durationMin = startedAt && endedAt ? Math.round((endedAt - startedAt) / 60000) : 0;

    return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
                <Link to="/dashboard" className="mono-label text-slate-500 hover:text-slate-900 inline-flex items-center gap-2 mb-6" data-testid="back-to-dashboard">
                    <ArrowLeft size={14} /> {t("report.back")}
                </Link>
                <div className="mono-label mb-3">/ {t("report.title")}</div>
                <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-3" data-testid="report-title">
                    {t(`practice.scenarios.${sess.scenario}`)}
                </h1>
                <p className="text-slate-500 mb-12">
                    {startedAt?.toLocaleString()} · {durationMin} min · {sess.language?.toUpperCase()}
                </p>

                {/* Overall score */}
                <div className="grid lg:grid-cols-12 gap-px bg-slate-100 border border-slate-200 mb-12">
                    <div className="lg:col-span-5 bg-ink p-10 border-l-2 border-brand">
                        <div className="mono-label mb-4">/ {t("report.overall")}</div>
                        <div className="font-display text-8xl text-brand" data-testid="report-overall-score">
                            {scores.overall ?? 0}
                            <span className="text-3xl text-slate-400">/100</span>
                        </div>
                    </div>
                    <div className="lg:col-span-7 bg-ink p-10 grid grid-cols-2 gap-6">
                        <ScoreLine label={t("report.persuasion")} value={scores.persuasion} testid="score-persuasion" />
                        <ScoreLine label={t("report.clarity")} value={scores.clarity} testid="score-clarity" />
                        <ScoreLine label={t("report.confidence")} value={scores.confidence} testid="score-confidence" />
                        <ScoreLine label={t("report.expressiveness")} value={scores.expressiveness} testid="score-expressiveness" />
                    </div>
                </div>

                {/* Fillers */}
                <div className="grid lg:grid-cols-2 gap-px bg-slate-100 border border-slate-200 mb-12">
                    <div className="bg-ink p-10">
                        <div className="mono-label mb-4">/ {t("report.fillers")}</div>
                        {fillerEntries.length === 0 ? (
                            <p className="font-display text-3xl text-brand" data-testid="no-fillers">
                                {t("report.none")}
                            </p>
                        ) : (
                            <div className="space-y-3" data-testid="fillers-list">
                                {fillerEntries
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([w, n]) => (
                                        <div key={w} className="flex justify-between items-center border-b border-slate-200 pb-2">
                                            <span className="font-display text-xl">"{w}"</span>
                                            <span className="font-mono text-brand">×{n}</span>
                                        </div>
                                    ))}
                                <div className="pt-4 mono-label text-slate-500">
                                    TOTAL: <span className="text-brand font-mono">{fillers._total}</span> · RATE:{" "}
                                    <span className="text-brand font-mono">{scores.filler_rate_per_100w}/100w</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="bg-ink p-10">
                        <div className="mono-label mb-4">/ {t("report.advice_title")}</div>
                        <ul className="space-y-3 text-slate-700 text-sm font-light">
                            {generateAdvice(scores, fillers).map((a, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="text-brand mono-label mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                                    <span>{a}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Transcript */}
                <div className="bg-slate-50 border border-slate-200 p-10" data-testid="report-transcript">
                    <div className="mono-label mb-4">/ {t("report.transcript")}</div>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {sess.turns?.map((tu, i) => (
                            <div key={i} className="space-y-1 pb-3 border-b border-slate-200">
                                <div className="text-sm text-slate-800"><span className="mono-label text-slate-500 mr-2">YOU</span>{tu.user_text}</div>
                                <div className="text-sm text-brand"><span className="mono-label text-brand/60 mr-2">{aiName.toUpperCase()}</span>{tu.ai_text}</div>
                            </div>
                        ))}
                        {(!sess.turns || sess.turns.length === 0) && (
                            <p className="text-slate-500 text-sm">{sess.transcript_full || "—"}</p>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

function ScoreLine({ label, value, testid }) {
    const v = Math.round(value || 0);
    return (
        <div data-testid={testid}>
            <div className="mono-label mb-2">{label}</div>
            <div className="font-display text-4xl mb-2">{v}<span className="text-base text-slate-400">/100</span></div>
            <div className="h-1 bg-slate-100">
                <div className="h-full bg-brand transition-all" style={{ width: `${v}%` }} />
            </div>
        </div>
    );
}

function generateAdvice(scores, fillers) {
    const tips = [];
    if ((fillers._total || 0) > 5) tips.push("Reduce el uso de muletillas haciendo pausas intencionales en lugar de rellenos.");
    if ((scores.clarity || 0) < 60) tips.push("Estructura mejor tus frases. Una idea por oración.");
    if ((scores.confidence || 0) < 60) tips.push("Aumenta el contacto visual y mantén una postura abierta.");
    if ((scores.expressiveness || 0) < 60) tips.push("Varía el tono, gesticula más y evita la rigidez corporal.");
    if ((scores.persuasion || 0) < 60) tips.push("Usa ejemplos concretos y refuerza tus argumentos con datos.");
    if (tips.length === 0) tips.push("¡Excelente desempeño! Sigue practicando para mantener el nivel.");
    return tips;
}
